<template>

    <FlashNotifications ref="flash" />
    <div class="w-full bg-white dark:bg-gray-600">
        <div class="px-5 md:px-12 lg:px-20 xl:px-32 min-h-[80vh] relative flex flex-col xl:max-w-[85rem] mx-auto">
            <div class="mt-5">
                <p class="pb-1 text-gray-700 dark:text-gray-100">{{ I18N.application.calendar_settings.language_and_region }}</p>
                <div class="w-full sm:w-96 mt-2">
                    <TimelydoDropdown
                      :title="I18N.application.calendar_settings.language"
                      name="locale"
                      :selected_value="calendar_settings.locale"
                      :values="languages_list"
                      :opts="languageDropdownOptions"
                      @change="updateDropdownValue"
                    >
                    </TimelydoDropdown>
                </div>
                <div class="w-full sm:w-96 mt-2">
                    <TimelydoDropdown
                      :title="I18N.application.calendar_settings.country"
                      name="country_iso_code"
                      :selected_value="calendar_settings.country_iso_code"
                      :values="countries_list"
                      :opts="countryDropdownOptions"
                      @change="updateDropdownValue"
                    >
                    </TimelydoDropdown>
                </div>
                <div class="w-full sm:w-96 mt-2">
                    <TimelydoDropdown
                      :title="I18N.application.calendar_settings.date_format"
                      name="date_format"
                      :selected_value="calendar_settings.date_format"
                      :values="date_formats_list"
                      :opts="{}"
                      @change="updateDropdownValue"
                    >
                    </TimelydoDropdown>
                </div>
                <div class="w-full sm:w-96 mt-2">
                    <TimelydoDropdown
                      :title="I18N.application.calendar_settings.time_format"
                      name="time_format"
                      :selected_value="calendar_settings.time_format"
                      :values="time_formats_list"
                      :opts="{}"
                      @change="updateDropdownValue"
                    >
                    </TimelydoDropdown>
                </div>
            </div>
            
            <div class="mt-10">
                <p class="pb-3 text-gray-700 dark:text-white">{{ I18N.application.calendar_settings.timezone }}</p>
                <div class="items-center flex justify-between">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="setSecondaryTimezone" v-model="calendar_settings.display_secondary_time_zone" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                        {{ I18N.application.calendar_settings.display_secondary_timezone }}
                    </label>
                    <button @click="swapValues"
                        :class="{ 'pointer-events-none fill-gray-400': calendar_settings.display_secondary_time_zone == false , 'fill-gray-600 dark:fill-gray-300': calendar_settings.display_secondary_time_zone == true }"
                        class="w-10 h-10 ml-2 sm:hidden flex justify-center items-center hover:fill-gray-900 hover:bg-gray-100 focus:bg-gray-200 rounded-full dark:hover:bg-gray-500 dark:hover:fill-gray-100 ">
                        <svg class="w-6 h-6" viewBox="0 0 24 24"><path d="M4 7l1.41 1.41L8 5.83V13h2V5.83l2.59 2.58L14 7 9 2 4 7zm16 10l-1.41-1.41L16 18.17V11h-2v7.17l-2.59-2.58L10 17l5 5 5-5z"></path></svg>
                    </button>
                </div>
                <div class="flex items-center" >
                    <div class="w-full sm:w-fit">
                        <div class="flex mt-3 w-full sm:w-fit">
                            <div class="w-full sm:w-96">
                                <TimelydoDropdown
                                  :title="I18N.application.calendar_settings.primary_timezone"
                                  name="primary_timezone"
                                  :selected_value="calendar_settings.primary_timezone"
                                  :values="timezones_list"
                                  :opts="timezoneDropdownOptions"
                                  @change="updateDropdownValue">
                                </TimelydoDropdown>
                            </div> 
                            <div
                              @click="focusInputPrimary"
                              :class="{ 'border-b-[2px] border-blue-600 bg-gray-200 dark:bg-gray-400 ': isPrimaryInputFocused, 'bg-[whitesmoke] dark:bg-gray-700': !isPrimaryInputFocused }"
                              class="ml-5 hover:bg-gray-200 text-gray-600 hover:text-gray-900 rounded relative dark:text-gray-300 dark:hover:text-gray-100"
                            >
                              <label
                                v-if="!isPrimaryInputFocused && !calendar_settings.label_for_primary_timezone"
                                class="text-[14px] absolute px-2 pt-1.5 transition-transform duration-300 transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
                              >
                                {{ I18N.application.calendar_settings.label }} 
                              </label>
                              <label
                                v-else
                                :class="{ 'text-blue-600': isPrimaryInputFocused }"
                                class="text-[10px] absolute pl-5 pt-2 transition-transform duration-300 transform -translate-x-2 -translate-y-0"
                              >
                                {{ I18N.application.calendar_settings.label }}
                              </label>
                              <input
                                @input="changeLabel"
                                ref="inputFieldPrimary"
                                @focus="onPrimaryInputFocus"
                                @blur="onPrimaryInputBlur"
                                v-model="calendar_settings.label_for_primary_timezone"
                                class="bg-transparent text-sm w-[95px] border-none mt-6 px-3 py-0 focus:ring-transparent"
                                type="text"
                                autocomplete="off"
                                maxlength="7"
                              />
                            </div>
                        </div>  
                        <div class="my-2 flex">
                            <div class="w-full sm:w-96"
                                 :class="{ 'pointer-events-none text-gray-400 dark:text-gray-500': calendar_settings.display_secondary_time_zone == false }"
                            >
                                <TimelydoDropdown
                                  :title="I18N.application.calendar_settings.secondary_timezone"
                                  name="secondary_timezone"
                                  :selected_value="calendar_settings.secondary_timezone"
                                  :values="timezones_list"
                                  :opts="timezoneDropdownOptions"
                                  @change="updateDropdownValue">
                                </TimelydoDropdown>
                            </div>
                            <div
                              @click="focusInputSecondary"
                              :class="{ 'border-b-[2px] border-blue-600 bg-gray-200 dark:bg-gray-400': isSecondaryInputFocused, 'bg-[whitesmoke] dark:bg-gray-700': !isSecondaryInputFocused, 'pointer-events-none text-gray-400 dark:text-gray-500': calendar_settings.display_secondary_time_zone == false }"
                              class="ml-5 hover:bg-gray-200 text-gray-600 hover:text-gray-900 rounded relative dark:text-gray-300 dark:hover:text-gray-100"
                            >
                              <label
                                v-if="!isSecondaryInputFocused && !calendar_settings.label_for_secondary_timezone"
                                :class="{ 'text-gray-400 dark:text-gray-500': calendar_settings.display_secondary_time_zone == false }"
                                class="text-[14px] absolute px-2 pt-1.5 transition-transform duration-300 transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
                              >
                                {{ I18N.application.calendar_settings.label }}
                              </label>
                              <label
                                v-else
                                :class="{ 'text-blue-600': isSecondaryInputFocused }"
                                class="text-[10px] absolute pl-5 pt-2 transition-transform duration-300 transform -translate-x-2 -translate-y-0"
                              >
                                {{ I18N.application.calendar_settings.label }}
                              </label>
                              <input
                                @input="changeLabel"
                                ref="inputFieldSecondary"
                                @focus="onSecondaryInputFocus"
                                @blur="onSecondaryInputBlur"
                                v-model="calendar_settings.label_for_secondary_timezone"
                                class="bg-transparent text-sm w-[95px] border-none mt-6 px-3 py-0 focus:ring-transparent"
                                type="text"
                                autocomplete="off"
                                maxlength="7"
                              />
                            </div>
                        </div>
                    </div>
                    <div>
                        <button @click="swapValues"
                                :class="{ 'pointer-events-none fill-gray-400': calendar_settings.display_secondary_time_zone == false , 'fill-gray-600 dark:fill-gray-300': calendar_settings.display_secondary_time_zone == true }"
                                class="w-10 h-10 ml-2 hidden sm:flex justify-center items-center hover:fill-gray-900 hover:bg-gray-100 focus:bg-gray-200 rounded-full dark:hover:bg-gray-500 dark:hover:fill-gray-100 ">
                            <svg class="w-6 h-6" viewBox="0 0 24 24"><path d="M4 7l1.41 1.41L8 5.83V13h2V5.83l2.59 2.58L14 7 9 2 4 7zm16 10l-1.41-1.41L16 18.17V11h-2v7.17l-2.59-2.58L10 17l5 5 5-5z"></path></svg>
                        </button>
                    </div>
                </div>
            </div>
            <div class="mt-10 mb-5">
                <p class="pb-3 text-gray-700 dark:text-white">{{ I18N.application.calendar_settings.view_options }}</p>
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.show_declined_bookings" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                        {{ I18N.application.calendar_settings.show_declined_bookings }}
                    </label>
                </div>
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.show_past_bookings" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                       <!--                        TODO: ADD TRANSLATION-->
                        Show past bookings
                    </label>
                </div>
                
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.collapse_duplicate_events" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                        {{ I18N.application.calendar_settings.collapse_duplicate_bookings }}
                    </label>
                </div>
                
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.reduce_brightness_of_past_bookings" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                        {{ I18N.application.calendar_settings.reduce_brightness_past_bookings }}
                    </label>
                </div>
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.narrow_weekend" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                       {{ I18N.application.calendar_settings.show_narrow_weekend }}
                    </label>
                </div>
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.show_now_indicator" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                        {{ I18N.application.calendar_settings.show_now_indicator }}
                    </label>
                </div>
                <div class="items-center flex py-1">
                    <label class="text-sm text-gray-700 dark:text-gray-300 items-center flex">
                        <input @change="updateCalendarSettings" v-model="calendar_settings.show_only_work_week" type="checkbox" class="rounded-[2px] w-[19px] h-[19px] border-[2px] border-blue-600 focus:ring-transparent mr-2 focus:appearance-none focus:ring-0 focus:outline-0" name="event">
                        {{ I18N.application.calendar_settings.show_work_week }}
                    </label>
                </div>
                <div class="w-full sm:w-96 mt-2">
                    <TimelydoDropdown
                      :title="I18N.application.calendar_settings.start_week_on"
                      name="start_day_of_week"
                      :selected_value="calendar_settings.start_day_of_week"
                      :values="week_days_list"
                      :opts="{maximum_height: '110px'}"
                      @change="updateDropdownValue"
                    >
                    </TimelydoDropdown>
                </div>
                <div class="w-full sm:w-96 mt-2 mb-24">
                    <TimelydoDropdown
                      :title="I18N.application.calendar_settings.default_view "
                      name="default_view"
                      :selected_value="calendar_settings.default_view"
                      :values="views_list"
                      :opts="{maximum_height: '110px'}"
                      @change="updateDropdownValue"
                    >
                    </TimelydoDropdown>
                </div>
            </div>
        </div>
    </div>
  
</template>

<script>
import RequestMixin from "controllers/vue_components/mixins/request"
import FlashNotifications from "controllers/vue_components/flash_toast"
import TimelydoDropdown from "controllers/vue_components/fields/timelydo_dropdown"
import Constants from "controllers/vue_components/mixins/constants";

export default {
    name: 'CalendarSettings',
    mixins: [RequestMixin, Constants],
    props: ['time_zone_list_json', 'week_days_list_json', 'languages_list_json', 'countries_list_json', 'calendar_settings_json', 'date_formats_json', 'time_formats_json', 'views_list_json'],
    components: {
        FlashNotifications, TimelydoDropdown
    },
    data() {
        return {
            languages_list: [],
            countries_list: [],
            date_formats_list: [],
            time_formats_list: [],
            week_days_list: [],
            views_list: [],
            calendar_settings: {},
            isPrimaryInputFocused: false,
            isSecondaryInputFocused: false,
            timezones_list: [],
            countryDropdownOptions: {
                placeholder: 'Select a country',
                classes: 'custom-dropdown',
                search_able: true,
                maximum_height: '500px',
            },

            languageDropdownOptions: {
                placeholder: 'Select a language',
                classes: 'custom-dropdown',
                search_able: true,
                maximum_height: '300px',
            },

            timezoneDropdownOptions: {
                placeholder: 'Select a timezone',
                classes: 'custom-dropdown',
                search_able: true,
                maximum_height: '300px',
            }
        };
    },

    methods: {
        onPrimaryInputFocus() {
            this.isPrimaryInputFocused = true;
        },
        onPrimaryInputBlur() {
            this.isPrimaryInputFocused = false;
        },
        onSecondaryInputFocus() {
            this.isSecondaryInputFocused = true;
        },
        onSecondaryInputBlur() {
            this.isSecondaryInputFocused = false;
        },
        focusInputPrimary() {
            this.$refs.inputFieldPrimary.focus();
        },
        focusInputSecondary() {
            this.$refs.inputFieldSecondary.focus();
        },
        prepareTimezoneList() {
            const format = this.calendar_settings.time_format;

            this.timezones_list = this.timezones_list.map(timezone => {
                const formattedTime = format === '12_hours'
                    ? timezone.current_time['12_hours']
                    : timezone.current_time['24_hours'];

                return {
                    ...timezone,
                    rightText: formattedTime, // Add a new attribute
                };
            });
        },

        updateDropdownValue(value) {
            let temp = this.calendar_settings.time_format;
            let attr = Object.keys(value)[0];
            this.calendar_settings[attr] = value[attr];
            this.updateCalendarSettings();

            if (temp !== this.calendar_settings.time_format){
                this.prepareTimezoneList();
            }
        },

        async updateCalendarSettings() {
            await this.axiosRequest('PUT', `/api/v1/calendar/settings`, {calendar_setting: this.calendar_settings}, true)
        },
        async changeLabel() {
            await this.axiosRequest('PUT', `/api/v1/calendar/settings`, {calendar_setting: this.calendar_settings}, false)
        },
        setSecondaryTimezone() {
            if (this.calendar_settings.display_secondary_time_zone == false) {
                this.calendar_settings.secondary_timezone = null;
                this.calendar_settings.label_for_secondary_timezone = null;
                this.updateCalendarSettings();
            }
        },
        swapValues() {
            if (this.calendar_settings.display_secondary_time_zone == true && this.calendar_settings.secondary_timezone != null) {
                [this.calendar_settings.secondary_timezone, this.calendar_settings.primary_timezone] = [this.calendar_settings.primary_timezone, this.calendar_settings.secondary_timezone];
                [this.calendar_settings.label_for_secondary_timezone, this.calendar_settings.label_for_primary_timezone] = [this.calendar_settings.label_for_primary_timezone, this.calendar_settings.label_for_secondary_timezone];
                this.updateCalendarSettings();
            }
        }
    },
    computed: {},
    created() {
        this.calendar_settings = JSON.parse(this.calendar_settings_json)
        this.languages_list = JSON.parse(this.languages_list_json)
        this.countries_list = JSON.parse(this.countries_list_json)
        this.date_formats_list = JSON.parse(this.date_formats_json)
        this.time_formats_list = JSON.parse(this.time_formats_json)
        this.timezones_list = JSON.parse(this.time_zone_list_json)
        this.views_list = JSON.parse(this.views_list_json)
        this.prepareTimezoneList();
        this.week_days_list = JSON.parse(this.week_days_list_json)
        if (this.calendar_settings.language) {
            this.calendar_settings.locale = this.calendar_settings.language;
        }
    }
};
</script>
